import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  Users, 
  ShoppingCart, 
  Settings as SettingsIcon,
  Bell,
  Search,
  LogOut,
  Home,
  ChevronDown,
  Folder,
  Palette,
  Tag
} from 'lucide-react';
import { useAuth } from '../context/AuthContext'; // Import AuthContext

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: authUser, logout } = useAuth(); // Get user and logout from context
  
  const navItems = [
    { icon: <LayoutDashboard size={20} />, label: 'Dashboard', path: '/dashboard' },
    { icon: <Folder size={20} />, label: 'Categories', path: '/categories' },
    { icon: <Palette size={20} />, label: 'Attributes', path: '/attributes' },
    { icon: <Package size={20} />, label: 'Products', path: '/products' },
    { icon: <ShoppingCart size={20} />, label: 'Orders', path: '/orders' },
    { icon: <Users size={20} />, label: 'Customers', path: '/customers' },
    { icon: <SettingsIcon size={20} />, label: 'Settings', path: '/settings' },
  ];

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout(); // Use logout from context
      navigate('/login');
    }
  };

  // Get user from AuthContext first, fallback to localStorage
  const user = authUser || JSON.parse(localStorage.getItem('maechi_user') || '{}');

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white shadow-xl border-r border-gray-100">
        <div className="p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Home className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Maechi<span className="text-blue-600">.in</span></h1>
              <p className="text-gray-500 text-sm">Admin Panel</p>
            </div>
          </div>
        </div>

        <nav className="p-4">
          {navItems.map((item, index) => {
            const isActive = location.pathname === item.path || 
                           (item.path === '/dashboard' && location.pathname === '/');
            return (
              <Link
                key={index}
                to={item.path}
                className={`flex items-center px-4 py-3 mb-2 rounded-xl transition-all ${isActive
                  ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-500' 
                  : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
                }`}
              >
                <span className="mr-3">{item.icon}</span>
                <span className="font-medium">{item.label}</span>
                {isActive && (
                  <span className="ml-auto w-2 h-2 bg-blue-500 rounded-full"></span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="absolute bottom-0 w-64 p-6 border-t border-gray-100">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-between px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-all"
          >
            <div className="flex items-center">
              <LogOut size={20} className="mr-3" />
              <span className="font-medium">Logout</span>
            </div>
            <ChevronDown size={18} className="text-red-400" />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between">
          <div className="flex-1 max-w-2xl">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search orders, products, customers..."
                className="w-full pl-10 pr-4 py-2.5 bg-gray-100 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white"
              />
            </div>
          </div>

          <div className="flex items-center space-x-6">
            <button className="relative text-gray-600 hover:text-gray-900">
              <Bell size={22} />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>

            <div 
              className="flex items-center space-x-3 cursor-pointer" 
              onClick={() => navigate('/settings')}
            >
              <div className="text-right">
                <p className="font-semibold text-gray-900">{user?.name || 'Admin User'}</p>
                <p className="text-gray-500 text-sm">{user?.email || 'admin@maechi.in'}</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-teal-400 rounded-full flex items-center justify-center text-white font-bold">
                {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-8">
          {/* Dynamic Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900">
              {location.pathname === '/' || location.pathname === '/dashboard' ? 'Dashboard Overview' : 
               location.pathname === '/categories' ? 'Categories Management' :
               location.pathname === '/attributes' ? 'Attributes Management' :
               location.pathname === '/products' ? 'Product Management' :
               location.pathname === '/orders' ? 'Order Management' :
               location.pathname === '/customers' ? 'Customer Management' :
               location.pathname === '/settings' ? 'Store Settings' : ''}
            </h2>
            <p className="text-gray-600 mt-2">
              {location.pathname === '/' || location.pathname === '/dashboard' ? 'Welcome back! Here\'s what\'s happening today.' : 
               location.pathname === '/categories' ? 'Manage product categories and subcategories.' :
               location.pathname === '/attributes' ? 'Manage product attributes like colors, sizes, materials.' :
               location.pathname === '/products' ? 'Manage your product inventory and listings.' :
               location.pathname === '/orders' ? 'View and manage customer orders.' :
               location.pathname === '/customers' ? 'Analyze and manage your customer base.' :
               location.pathname === '/settings' ? 'Configure your store settings and preferences.' : ''}
            </p>
          </div>
          
          {/* Page Content Goes Here */}
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;